import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { getListingBySlug, ListingContent, DocumentItem } from '@/data/listing-details.data';
import { ArrowLeft, Heart, Share2, Flag, FileText, Phone, Mail, Download, CheckCircle, Lock, Unlock, ShoppingCart, Crown, User, Loader2, Send, FolderLock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import { ShareDialog } from '@/components/listings/ShareDialog';
import { ContactSellerDialog } from '@/components/listings/ContactSellerDialog';
import { ReportDialog } from '@/components/listings/ReportDialog';
import { FinancingOptionDialog, FinancingOptionData } from '@/components/listings/FinancingOptionDialog';
import { useToast } from '@/hooks/use-toast';
import { useNdaGate } from '@/hooks/useNdaGate';
import { useFavorites } from '@/hooks/useFavorites';
import { useSubscription } from '@/hooks/useSubscription';
import { auth } from '@/lib/firebase';
import { usePublicProfiles } from '@/hooks/usePublicProfiles';
import { postApi } from '@/lib/api';
import { useContext } from "react";
import { RealListingContext } from "@/contexts/RealListingContext";

const TRANSLATIONS = {
  en: {
    backToSearch: 'Back to search',
    askingPrice: 'Asking Price',
    cashFlow: 'Cash Flow (SDE)',
    grossRevenue: 'Gross Revenue',
    sde: 'SDE',
    ebitda: 'EBITDA',
    realEstate: 'Real Estate',
    established: 'Established',
    ffe: 'FF&E',
    inventory: 'Inventory',
    signNda: 'Sign NDA',
    save: 'Save',
    share: 'Share',
    report: 'Report',
    businessDescription: 'Business Description',
    detailedInfo: 'Detailed Information',
    financingOptions: 'Pre-Financing Options',
    documents: 'Available Documents',
    premiumDocuments: 'Premium Documents',
    unlockDocuments: 'Unlock by signing NDA',
    documentsUnlocked: 'Documents unlocked - Click to download',
    download: 'Download',
    locked: 'Locked',
    brokerContact: 'Broker Contact',
    sellerContact: 'Direct from Owner',
    offMarketBadge: 'OFF MARKET',
    contactBroker: 'Contact Broker',
    contactSeller: 'Contact Owner',
    addedToFavorites: 'Added to favorites',
    removedFromFavorites: 'Removed from favorites',
    buyDocument: 'Buy for',
    includedInPlan: 'Included in your plan',
    upgradeRequired: 'Upgrade to access',
    viewPlans: 'View Plans',
    subscriptionRequired: 'Subscription required to access this feature',
    essentialRequired: 'Available for Essential & Essential Plus subscribers',
    essentialPlusRequired: 'Included in Essential Plus plan',
    purchaseFor: 'Purchase for',
    expertsTitle: 'Experts Available in This Sector',
    expertsSubtitle: 'Internal partners specialized in this field',
    consultantsTitle: 'Consultants in This Field',
    consultantsSubtitle: 'Independent consultants for this type of acquisition',
    viewProfile: 'View Profile',
    connect: 'Connect',
    noExperts: 'No experts available in this sector yet.',
    noConsultants: 'No consultants available in this field yet.',
    reportsTitle: 'Analytical Reports',
    reportsSubtitle: 'Professional reports generated by AcquizGroup',
    unlockReport: 'Unlock',
  },
  fr: {
    backToSearch: 'Retour à la recherche',
    askingPrice: 'Prix Demandé',
    cashFlow: 'Flux de trésorerie (SDE)',
    grossRevenue: 'Revenus Bruts',
    sde: 'SDE',
    ebitda: 'EBITDA',
    realEstate: 'Immobilier',
    established: 'Année Établie',
    ffe: 'FF&E',
    inventory: 'Inventaire',
    signNda: 'Signer le NDA',
    save: 'Sauvegarder',
    share: 'Partager',
    report: 'Signaler',
    businessDescription: "Description de l'Entreprise",
    detailedInfo: 'Informations Détaillées',
    financingOptions: 'Options de Pré-Financement',
    documents: 'Documents Disponibles',
    premiumDocuments: 'Documents Premium',
    unlockDocuments: 'Déverrouiller en signant le NDA',
    documentsUnlocked: 'Documents déverrouillés - Cliquez pour télécharger',
    download: 'Télécharger',
    locked: 'Verrouillé',
    brokerContact: 'Contact du Courtier',
    sellerContact: 'Directement du Propriétaire',
    offMarketBadge: 'HORS MARCHÉ',
    contactBroker: 'Contacter le Courtier',
    contactSeller: 'Contacter le Propriétaire',
    addedToFavorites: 'Ajouté aux favoris',
    removedFromFavorites: 'Retiré des favoris',
    buyDocument: 'Acheter pour',
    includedInPlan: 'Inclus dans votre forfait',
    upgradeRequired: 'Mise à niveau requise',
    viewPlans: 'Voir les forfaits',
    subscriptionRequired: 'Abonnement requis pour accéder à cette fonctionnalité',
    essentialRequired: 'Disponible pour les abonnés Essentiel et Essentiel Plus',
    essentialPlusRequired: 'Inclus dans le forfait Essentiel Plus',
    purchaseFor: 'Acheter pour',
    expertsTitle: 'Experts disponibles dans ce secteur',
    expertsSubtitle: 'Partenaires internes spécialisés dans ce domaine',
    consultantsTitle: 'Consultants dans ce domaine',
    consultantsSubtitle: "Consultants indépendants pour ce type d'acquisition",
    viewProfile: 'Voir le profil',
    connect: 'Contacter',
    noExperts: 'Aucun expert disponible dans ce secteur pour le moment.',
    noConsultants: 'Aucun consultant disponible dans ce domaine pour le moment.',
    reportsTitle: 'Rapports analytiques',
    reportsSubtitle: 'Rapports professionnels générés par AcquizGroup',
    unlockReport: 'Déverrouiller',
  },
};

// ─── Données fixes Analytical Reports (Price IDs Stripe confirmés) ───────────
const getAnalyticalReports = (language: string) => [
  {
    name: language === 'en' ? 'Deal Quality Report' : 'Rapport de Qualité du Deal',
    price: 49,
    priceId: 'price_1Td1KACkUJGKtqQAHxG44n8u',
    desc: language === 'en'
      ? 'TOV score, adjusted multiple, sector comparison'
      : 'Score TOV, multiple ajusté, comparaison sectorielle',
  },
  {
    name: language === 'en' ? 'Financing Feasibility Report' : 'Rapport de Faisabilité Financière',
    price: 79,
    priceId: 'price_1Td1KpCkUJGKtqQAwzvIdGzV',
    desc: language === 'en'
      ? 'Annuity / SBA / LBO simulation, DSCR'
      : 'Simulation Annuity / SBA / LBO, DSCR',
  },
  {
    name: language === 'en' ? 'Market Comparison Report' : 'Rapport de Comparaison de Marché',
    price: 99,
    priceId: 'price_1Td1LSCkUJGKtqQAjWK6W8YR',
    desc: language === 'en'
      ? 'Sector multiples, vs similar closed deals'
      : 'Multiples sectoriels, vs deals similaires conclus',
  },
  {
    name: language === 'en' ? 'Market Study' : 'Étude de Marché',
    price: 199,
    priceId: 'price_1Td1NlCkUJGKtqQAoHqgWzsC',
    desc: language === 'en'
      ? 'AI market analysis for this sector and region'
      : 'Analyse IA pour ce secteur et cette région',
  },
];

// ─── Données fixes Premium Documents (Price IDs Stripe confirmés) ─────────────
const getPremiumDocuments = (language: string) => [
  {
    name: language === 'en' ? 'Due Diligence Pack' : 'Pack Due Diligence',
    price: 199,
    priceId: 'price_1Td1LxCkUJGKtqQAbRRz5d7u',
    desc: language === 'en'
      ? 'Complete due diligence documentation package'
      : 'Package complet de documentation due diligence',
  },
  {
    name: language === 'en' ? 'Acquisition Intelligence Report' : "Rapport Intelligence Acquisition",
    price: 399,
    priceId: 'price_1Td1MfCkUJGKtqQAp8LEmfH1',
    desc: language === 'en'
      ? 'Deep buyer-side acquisition analysis'
      : 'Analyse approfondie côté acquéreur',
  },
  {
    name: language === 'en' ? 'Full Intelligence Bundle' : 'Bundle Intelligence Complet',
    price: 599,
    priceId: 'price_1Td1NJCkUJGKtqQATaaDpqwe',
    desc: language === 'en'
      ? 'Market Study + Strategic Plan + Acquisition Intel bundled'
      : 'Étude de Marché + Plan Stratégique + Acquisition Intel groupés',
  },
];

export default function ListingDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { toast } = useToast();
  const { isFavorite, toggleFavorite: toggleFavoriteHook, loading: favoritesLoading } = useFavorites();
  const { tier: userSubscription, loading: subscriptionLoading } = useSubscription();
  const [selectedImage, setSelectedImage] = useState(0);
  const [hasSignedNda, setHasSignedNda] = useState(false);
  const [purchasingDocument, setPurchasingDocument] = useState<string | null>(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [showContactSellerDialog, setShowContactSellerDialog] = useState(false);
  const [contactRequestSent, setContactRequestSent] = useState(false);
  const [selectedFinancingOption, setSelectedFinancingOption] = useState<FinancingOptionData | null>(null);

  // ─── CORRECTION 1 : helper unifié, une seule définition ──────────────────────
  const handleOneTimeCheckout = async (item: { priceId: string; description: string; price: number }) => {
    if (!auth.currentUser) { navigate('/login'); return; }
    try {
      setPurchasingDocument(item.priceId);
      const origin = window.location.origin;
      const data: any = await postApi('/payments/checkout', {
        type: 'one_time',
        priceId: item.priceId,
        description: item.description,
        listingSlug: slug,
        successUrl: `${origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${origin}/listings/${slug}`,
      });
      const url = data?.url ?? data?.data?.url;
      if (url) window.open(url, '_blank');
      else throw new Error('No checkout URL');
    } catch {
      toast({ title: language === 'en' ? 'Payment Error' : 'Erreur de paiement', variant: 'destructive' });
    } finally {
      setPurchasingDocument(null);
    }
  };

  const appId = import.meta.env.VITE_APP_ID || 'default-app-id';
  const { hasSignedNdaForDeal, loading: ndaLoading } = useNdaGate(appId);

  const t = TRANSLATIONS[language as keyof typeof TRANSLATIONS] || TRANSLATIONS.en;
  const injectedListing = useContext(RealListingContext);
  const listing = injectedListing ?? (slug ? getListingBySlug(slug) : undefined);
  const dealId = listing?.id || slug || 'unknown-deal';

  const { profiles: expertProfiles, loading: expertsLoading } = usePublicProfiles(['partner']);
  const { profiles: consultantProfiles, loading: consultantsLoading } = usePublicProfiles(['consultant']);

  const _sectorContent: any = listing ? (listing as any)[language] || (listing as any).en : null;
  const dealSector = _sectorContent?.detailedInfo
    ?.find((i: any) =>
      i.label.toLowerCase().includes('industry') ||
      i.label.toLowerCase().includes('secteur') ||
      i.label.toLowerCase().includes('sector')
    )?.value || '';

  const displayedExperts = expertProfiles.slice(0, 3);
  const displayedConsultants = consultantProfiles.slice(0, 3);

  // Données calculées une seule fois
  const analyticalReports = getAnalyticalReports(language);
  const premiumDocuments = getPremiumDocuments(language);

  useEffect(() => {
  const checkNdaStatus = async () => {
    if (!auth.currentUser || !slug) return;
    const signed = await hasSignedNdaForDeal(slug);
    setHasSignedNda(signed);
  };
  checkNdaStatus();
}, [slug, hasSignedNdaForDeal]);

  useEffect(() => {
    const checkContactRequest = async () => {
      if (!auth.currentUser || !dealId) return;
      try {
        const { collection, query, where, getDocs, limit } = await import('firebase/firestore');
        const { db } = await import('@/lib/firebase');
        const contactRef = collection(db, 'contactRequests');
        const q = query(contactRef, where('buyerId', '==', auth.currentUser.uid), where('dealId', '==', dealId), limit(1));
        const snap = await getDocs(q);
        if (!snap.empty) setContactRequestSent(true);
      } catch {}
    };
    checkContactRequest();
  }, [dealId]);

  useEffect(() => {
    const documentPurchased = searchParams.get('document_purchased');
    const purchaseCanceled = searchParams.get('purchase_canceled');
    if (documentPurchased) {
      toast({
        title: language === 'en' ? 'Purchase Successful!' : 'Achat réussi!',
        description: language === 'en' ? 'Your document is now available for download.' : 'Votre document est maintenant disponible au téléchargement.',
      });
      navigate(`/listings/${slug}`, { replace: true });
    }
    if (purchaseCanceled) {
      toast({
        title: language === 'en' ? 'Purchase Canceled' : 'Achat annulé',
        description: language === 'en' ? 'Your purchase was canceled. No charges were made.' : "Votre achat a été annulé. Aucun frais n'a été facturé.",
        variant: 'destructive',
      });
      navigate(`/listings/${slug}`, { replace: true });
    }
  }, [searchParams, slug, language, toast, navigate]);

  const handleSignNda = () => {
    if (subscriptionLoading) {
      toast({ title: language === 'en' ? 'Checking subscription' : 'Vérification du forfait', description: language === 'en' ? 'Please try again in a moment.' : 'Veuillez réessayer dans un instant.' });
      return;
    }
    if (!auth.currentUser || userSubscription === null || userSubscription === 'visitor') {
      toast({ title: language === 'en' ? 'Subscription required' : 'Abonnement requis', description: t.subscriptionRequired, variant: 'destructive' });
      navigate('/buyer/payment');
      return;
    }
    const params = new URLSearchParams({
      deal: slug || '',
      dealName: content.title || '',
      industry: content.detailedInfo?.find(i => i.label.toLowerCase().includes('industry') || i.label.toLowerCase().includes('secteur'))?.value || '',
      recipientType: 'buyer',
    });
    navigate(`/tools/nda-generator?${params.toString()}`);
  };

  if (!listing) {
    return (
      <div className="min-h-screen bg-[#f5f7fa] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#2c3e50] mb-4">{language === 'en' ? 'Listing not found' : 'Annonce introuvable'}</h1>
          <Button onClick={() => navigate('/listings')}><ArrowLeft className="mr-2 h-4 w-4" />{t.backToSearch}</Button>
        </div>
      </div>
    );
  }

  const content: ListingContent = listing[language as keyof typeof listing] as ListingContent || listing.en;
  const images = [content.mainImage, ...content.thumbnails];
  const isCurrentFavorite = slug ? isFavorite(slug) : false;

  const handleToggleFavorite = async () => {
    if (!slug || favoritesLoading) return;
    const wasFavorite = isFavorite(slug);
    const nowFavorite = await toggleFavoriteHook(slug);
    if (wasFavorite && !nowFavorite) { toast({ title: t.removedFromFavorites }); }
    else if (!wasFavorite) { toast({ title: t.addedToFavorites }); }
  };

  const getBadgeClass = (type: string) => {
    switch (type) {
      case 'gold': return 'bg-gradient-to-r from-[#b8924f] to-[#d4af6a] text-white';
      case 'green': return 'bg-gradient-to-r from-[#27ae60] to-[#2ecc71] text-white';
      case 'blue': return 'bg-gradient-to-r from-[#3498db] to-[#2980b9] text-white';
      default: return 'bg-gray-200 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f7fa]">
      <nav className="bg-[#2c3e50] py-4 shadow-md">
        <div className="max-w-[1200px] mx-auto px-5 flex justify-between items-center">
          <button onClick={() => navigate('/listings')} className="bg-white/10 text-white border border-white/30 px-5 py-2 rounded-full text-sm transition-all hover:bg-white/20">
            ← {t.backToSearch}
          </button>
          <div className="text-white text-lg font-light tracking-[3px]">
            ACQUIZ<span className="bg-gradient-to-r from-[#b8924f] to-[#d4af6a] bg-clip-text text-transparent">GROUP</span>
          </div>
        </div>
      </nav>

      <div className="max-w-[1200px] mx-auto px-5 py-8">
        <div className="bg-white p-8 rounded-xl mb-5 shadow-sm">
          <h1 className="text-3xl font-bold text-[#2c3e50] mb-2">{content.title}</h1>
          <div className="text-[#7f8c8d] text-base mb-5">📍 {content.location}</div>
          <div className="flex gap-2 flex-wrap">
            {content.badges.map((badge, index) => (
              <span key={index} className={`inline-block px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide ${getBadgeClass(badge.type)}`}>{badge.label}</span>
            ))}
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl mb-5 shadow-sm">
          <div className="w-full h-[400px] rounded-lg bg-cover bg-center mb-4" style={{ backgroundImage: `url('${images[selectedImage]}')` }} />
          <div className="flex gap-2 overflow-x-auto pb-2">
            {images.map((img, index) => (
              <div key={index} onClick={() => setSelectedImage(index)} className={`w-[100px] h-[75px] rounded-lg bg-cover bg-center cursor-pointer transition-all flex-shrink-0 ${selectedImage === index ? 'border-2 border-[#b8924f]' : 'opacity-70 hover:opacity-100'}`} style={{ backgroundImage: `url('${img}')` }} />
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-5">

            {/* Price Card */}
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="text-center pb-5 border-b-2 border-[#ecf0f1] mb-5">
                <div className="text-sm text-[#7f8c8d] uppercase tracking-wide mb-2">{t.askingPrice}</div>
                <div className="text-4xl font-bold bg-gradient-to-r from-[#b8924f] to-[#d4af6a] bg-clip-text text-transparent">{content.financials.askingPrice}</div>
              </div>
              <div className="space-y-3 mb-6">
                {[
                  { label: t.cashFlow, value: content.financials.cashFlow },
                  { label: t.grossRevenue, value: content.financials.grossRevenue },
                  { label: t.sde, value: content.financials.sde },
                  { label: t.ebitda, value: content.financials.ebitda },
                  { label: t.realEstate, value: content.financials.realEstate },
                  { label: t.established, value: content.financials.established },
                  { label: t.ffe, value: content.financials.ffe },
                  { label: t.inventory, value: content.financials.inventory },
                ].map((item, index) => (
                  <div key={index} className="flex justify-between items-center py-3 border-b border-[#f5f5f5] last:border-0">
                    <span className="text-sm text-[#7f8c8d] font-medium">{item.label}:</span>
                    <span className="text-base text-[#2c3e50] font-semibold">{item.value}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-col gap-3 pt-6 border-t-2 border-[#ecf0f1]">
                <Button
                  className={`w-full transition-all ${hasSignedNda ? 'bg-green-600 hover:bg-green-700' : 'bg-gradient-to-r from-[#b8924f] to-[#d4af6a] hover:shadow-lg hover:-translate-y-0.5'}`}
                  onClick={handleSignNda}
                  disabled={hasSignedNda || ndaLoading}
                >
                  {hasSignedNda ? <><CheckCircle className="mr-2 h-4 w-4" />{language === 'en' ? 'NDA Signed' : 'NDA Signé'}</> : <>📩 {t.signNda}</>}
                </Button>
               {hasSignedNda && (
                  <>
                    <Button
                      className="w-full bg-[#1D314D] hover:bg-[#2E4663] text-white"
                      onClick={() => navigate(`/deal-room/${dealId}`)}
                    >
                      <FolderLock className="mr-2 h-4 w-4" />{language === 'en' ? 'View Deal Room' : 'Voir le Deal Room'}
                    </Button>
                    {contactRequestSent ? (
                      <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white cursor-default" disabled>
                        <CheckCircle className="mr-2 h-4 w-4" />{language === 'en' ? 'Request Sent' : 'Demande envoyée'}
                      </Button>
                    ) : (
                      <Button className="w-full bg-green-600 hover:bg-green-700 text-white" onClick={() => setShowContactSellerDialog(true)}>
                        <Send className="mr-2 h-4 w-4" />{language === 'en' ? 'Contact Seller' : 'Contacter le vendeur'}
                      </Button>
                    )}
                  </>
                )}
                <div className="flex gap-3">
                  <Button variant="outline" className={`flex-1 ${isCurrentFavorite ? 'border-destructive bg-destructive/10 text-destructive' : ''}`} onClick={handleToggleFavorite}>
                    <Heart className={`mr-2 h-4 w-4 ${isCurrentFavorite ? 'fill-current text-destructive' : ''}`} />{t.save}
                  </Button>
                  <Button variant="outline" className="flex-1" onClick={() => setShowShareDialog(true)}>
                    <Share2 className="mr-2 h-4 w-4" />{t.share}
                  </Button>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h2 className="text-xl font-bold text-[#2c3e50] pb-3 border-b-2 border-[#b8924f] mb-5">{t.businessDescription}</h2>
              <div className="space-y-4">
                {content.description.map((paragraph, index) => (
                  <p key={index} className="leading-7 text-[#555]">{paragraph}</p>
                ))}
              </div>
            </div>

            {/* Detailed Info */}
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h2 className="text-xl font-bold text-[#2c3e50] pb-3 border-b-2 border-[#b8924f] mb-5">{t.detailedInfo}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {content.detailedInfo.map((item, index) => (
                  <div key={index} className="p-4 bg-[#f8f9fa] rounded-lg">
                    <span className="text-xs text-[#7f8c8d] uppercase tracking-wide block mb-1">{item.label}</span>
                    <span className="text-base text-[#2c3e50] font-semibold">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Regular Documents */}
            <div className={`bg-white p-6 rounded-xl shadow-sm ${hasSignedNda ? 'ring-2 ring-green-500/20' : ''}`}>
              <div className="flex items-center justify-between pb-3 border-b-2 border-[#b8924f] mb-5">
                <h2 className="text-xl font-bold text-[#2c3e50]">{t.documents}</h2>
                {hasSignedNda
                  ? <span className="flex items-center gap-1 text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full"><Unlock className="w-3 h-3" />{language === 'en' ? 'Unlocked' : 'Déverrouillé'}</span>
                  : <span className="flex items-center gap-1 text-xs text-[#7f8c8d] bg-[#ecf0f1] px-2 py-1 rounded-full"><Lock className="w-3 h-3" />{t.locked}</span>
                }
              </div>
              <div className="space-y-3">
                {content.documents.filter(doc => !doc.isPremium).map((doc, index) => (
                  <div
                    key={index}
                   onClick={() => {
                      if (hasSignedNda) {
                        if (doc.url) {
                          window.open(doc.url, "_blank", "noopener,noreferrer");
                        } else {
                          toast({ title: language === 'en' ? 'Download Started' : 'Téléchargement démarré', description: doc.name });
                        }
                      } else {
                        toast({ title: language === 'en' ? 'NDA Required' : 'NDA Requis', variant: 'destructive' });
                      }
                    }}
                    className={`flex items-center justify-between p-4 rounded-lg border transition-all ${hasSignedNda ? 'bg-green-50 border-green-200 hover:border-green-400 cursor-pointer' : 'bg-[#f8f9fa] border-[#ecf0f1] cursor-not-allowed opacity-70'}`}
                  >
                    <div className="flex items-center gap-3">
                      <FileText className={`w-5 h-5 ${hasSignedNda ? 'text-green-600' : 'text-[#b8924f]'}`} />
                      <span className={`text-sm font-medium ${hasSignedNda ? 'text-green-800' : 'text-[#2c3e50]'}`}>{doc.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#7f8c8d] bg-[#ecf0f1] px-2 py-1 rounded">{doc.type}</span>
                      {hasSignedNda ? <Download className="w-4 h-4 text-green-600" /> : <Lock className="w-4 h-4 text-[#7f8c8d]" />}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ─── CORRECTION 2 : Analytical Reports — onClick via handleOneTimeCheckout ── */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-[#b8924f]/20">
              <div className="pb-3 border-b-2 border-[#b8924f] mb-5">
                <h2 className="text-xl font-bold text-[#2c3e50]">{t.reportsTitle}</h2>
                <p className="text-xs text-[#7f8c8d] mt-1">{t.reportsSubtitle}</p>
              </div>
              <div className="space-y-3">
                {analyticalReports.map((report) => (
                  <div
                    key={report.priceId}
                    onClick={() => handleOneTimeCheckout({
                      priceId: report.priceId,
                      description: report.name,
                      price: report.price,
                    })}
                    className="flex items-start justify-between p-4 rounded-lg border border-[#ecf0f1] bg-[#f8f9fa] hover:border-[#b8924f]/50 hover:bg-[#b8924f]/5 cursor-pointer transition-all"
                  >
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <FileText className="w-5 h-5 text-[#b8924f] mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <span className="text-sm font-semibold text-[#2c3e50] block">{report.name}</span>
                        <span className="text-xs text-[#7f8c8d] block mt-0.5">{report.desc}</span>
                      </div>
                    </div>
                    <div className="ml-3 shrink-0">
                      {purchasingDocument === report.priceId
                        ? <Loader2 className="w-4 h-4 animate-spin text-[#b8924f]" />
                        : <span className="text-xs font-bold text-white bg-[#b8924f] px-3 py-1.5 rounded-full whitespace-nowrap">{t.unlockReport} ${report.price}</span>
                      }
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ─── CORRECTION 3 : Premium Documents — items fixes avec vrais Price IDs ── */}
            <div className="bg-white p-6 rounded-xl shadow-sm border-2 border-[#b8924f]/20">
              <div className="flex items-center gap-2 pb-3 border-b-2 border-[#b8924f] mb-5">
                <Crown className="w-5 h-5 text-[#b8924f]" />
                <h2 className="text-xl font-bold text-[#2c3e50]">{t.premiumDocuments}</h2>
              </div>
              <div className="space-y-3">
                {premiumDocuments.map((doc) => (
                  <div
                    key={doc.priceId}
                    onClick={() => handleOneTimeCheckout({
                      priceId: doc.priceId,
                      description: doc.name,
                      price: doc.price,
                    })}
                    className="flex items-start justify-between p-4 rounded-lg border border-[#ecf0f1] bg-[#f8f9fa] hover:border-[#b8924f]/50 hover:bg-[#b8924f]/5 cursor-pointer transition-all"
                  >
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <FileText className="w-5 h-5 text-[#b8924f] mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <span className="text-sm font-semibold text-[#2c3e50] block">{doc.name}</span>
                        <span className="text-xs text-[#7f8c8d] block mt-0.5">{doc.desc}</span>
                      </div>
                    </div>
                    <div className="ml-3 shrink-0">
                      {purchasingDocument === doc.priceId
                        ? <Loader2 className="w-4 h-4 animate-spin text-[#b8924f]" />
                        : <span className="text-xs font-bold text-[#1D314D] bg-[#CEAB65] px-3 py-1.5 rounded-full whitespace-nowrap">
                            {language === 'en' ? 'Unlock' : 'Déverrouiller'} ${doc.price}
                          </span>
                      }
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* Sidebar */}
          <div className="space-y-5">

            {/* Seller/Broker Contact */}
            <div className="bg-white p-6 rounded-xl shadow-sm">
              {content.isOffMarket && content.seller ? (
                <>
                  <div className="flex items-center justify-center gap-2 mb-4 py-2 px-4 bg-gradient-to-r from-[#e74c3c] to-[#c0392b] rounded-full">
                    <User className="w-4 h-4 text-white" /><span className="text-xs font-bold text-white uppercase">{t.offMarketBadge}</span>
                  </div>
                  <h2 className="text-xl font-bold text-[#2c3e50] pb-3 border-b-2 border-[#b8924f] mb-5">{t.sellerContact}</h2>
                  <div className="text-center">
                    <div className="w-20 h-20 rounded-full mx-auto mb-3 bg-cover bg-center border-4 border-[#e74c3c]/20" style={{ backgroundImage: `url('${content.seller.image}')` }} />
                    <div className="text-lg font-semibold text-[#2c3e50]">{content.seller.name}</div>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="text-xl font-bold text-[#2c3e50] pb-3 border-b-2 border-[#b8924f] mb-5">{t.brokerContact}</h2>
                  <div className="text-center">
                    <div className="w-20 h-20 rounded-full mx-auto mb-3 bg-cover bg-center" style={{ backgroundImage: `url('${content.broker?.image}')` }} />
                    <div className="text-lg font-semibold text-[#2c3e50]">{content.broker?.name}</div>
                  </div>
                </>
              )}
            </div>

            {/* Financing Options */}
            <div className="bg-white p-6 rounded-xl shadow-sm sticky top-5">
              <h2 className="text-xl font-bold text-[#2c3e50] pb-3 border-b-2 border-[#b8924f] mb-5">{t.financingOptions}</h2>
              <div className="space-y-4">
                {content.financingOptions.map((option, index) => (
                  <div key={index} className="p-4 bg-[#f8f9fa] rounded-lg border-l-4 border-[#b8924f] cursor-pointer hover:bg-[#f0f0f0] transition-all" onClick={() => setSelectedFinancingOption(option)}>
                    <div className="text-sm font-semibold text-[#2c3e50]">{option.title}</div>
                    <div className="text-xs text-[#b8924f] mt-2 font-medium">{language === 'en' ? 'Details →' : 'Détails →'}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Experts */}
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="pb-3 border-b-2 border-[#b8924f] mb-4">
                <h2 className="text-lg font-bold text-[#2c3e50]">{t.expertsTitle}</h2>
                <p className="text-xs text-[#7f8c8d] mt-1">{t.expertsSubtitle}</p>
              </div>
              {expertsLoading
                ? <div className="flex justify-center py-4"><Loader2 className="h-6 w-6 animate-spin text-[#b8924f]" /></div>
                : displayedExperts.length === 0
                  ? <p className="text-xs text-[#7f8c8d] text-center py-4">{t.noExperts}</p>
                  : (
                    <div className="space-y-3">
                      {displayedExperts.map((profile) => (
                        <div key={profile.uid} className="flex items-center gap-3 p-3 rounded-lg bg-[#f8f9fa] border border-[#ecf0f1] hover:border-[#b8924f]/30 transition-all">
                          <div className="w-10 h-10 rounded-full bg-[#2c3e50] flex items-center justify-center text-white text-sm font-bold shrink-0" style={profile.photoURL ? { backgroundImage: `url('${profile.photoURL}')`, backgroundSize: 'cover' } : {}}>
                            {!profile.photoURL && `${profile.firstName?.[0] || ''}${profile.lastName?.[0] || ''}`}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-[#2c3e50] truncate">{profile.firstName} {profile.lastName}</p>
                            {profile.primarySector && <p className="text-xs text-[#7f8c8d] truncate">{profile.primarySector}</p>}
                          </div>
                          <button onClick={() => navigate(`/profile/public/${profile.uid}`)} className="text-xs text-[#b8924f] font-medium hover:underline shrink-0">{t.viewProfile}</button>
                        </div>
                      ))}
                    </div>
                  )
              }
            </div>

            {/* Consultants */}
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="pb-3 border-b-2 border-[#b8924f] mb-4">
                <h2 className="text-lg font-bold text-[#2c3e50]">{t.consultantsTitle}</h2>
                <p className="text-xs text-[#7f8c8d] mt-1">{t.consultantsSubtitle}</p>
              </div>
              {consultantsLoading
                ? <div className="flex justify-center py-4"><Loader2 className="h-6 w-6 animate-spin text-[#b8924f]" /></div>
                : displayedConsultants.length === 0
                  ? <p className="text-xs text-[#7f8c8d] text-center py-4">{t.noConsultants}</p>
                  : (
                    <div className="space-y-3">
                      {displayedConsultants.map((profile) => (
                        <div key={profile.uid} className="flex items-center gap-3 p-3 rounded-lg bg-[#f8f9fa] border border-[#ecf0f1] hover:border-[#b8924f]/30 transition-all">
                          <div className="w-10 h-10 rounded-full bg-[#2c3e50] flex items-center justify-center text-white text-sm font-bold shrink-0" style={profile.photoURL ? { backgroundImage: `url('${profile.photoURL}')`, backgroundSize: 'cover' } : {}}>
                            {!profile.photoURL && `${profile.firstName?.[0] || ''}${profile.lastName?.[0] || ''}`}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-[#2c3e50] truncate">{profile.firstName} {profile.lastName}</p>
                            {profile.primarySector && <p className="text-xs text-[#7f8c8d] truncate">{profile.primarySector}</p>}
                            {profile.city && <p className="text-xs text-[#7f8c8d] truncate">📍 {profile.city}</p>}
                          </div>
                          <button onClick={() => navigate(`/messages?to=${profile.uid}`)} className="text-xs text-white bg-[#b8924f] px-2 py-1 rounded-full font-medium hover:bg-[#a07840] transition-colors shrink-0">{t.connect}</button>
                        </div>
                      ))}
                    </div>
                  )
              }
            </div>

          </div>
        </div>
      </div>

      <ShareDialog open={showShareDialog} onOpenChange={setShowShareDialog} title={content.title} url={`/listings/${slug}`} />
      <ReportDialog open={showReportDialog} onOpenChange={setShowReportDialog} listingTitle={content.title} listingSlug={slug || ''} />
      <FinancingOptionDialog open={!!selectedFinancingOption} onOpenChange={(open) => !open && setSelectedFinancingOption(null)} option={selectedFinancingOption} listingTitle={content.title} />
      <ContactSellerDialog open={showContactSellerDialog} onOpenChange={setShowContactSellerDialog} dealId={dealId} dealName={content?.title} language={language} onRequestSent={() => setContactRequestSent(true)} />
    </div>
  );
}